<template>
  <div class="vcl-input" :style="containerStyles">
    <div class="vcl-input__label-row">
      <label v-if="$slots.label" ref="label" class="vcl-input__label" :for="_uid+'VpInput'">
        <slot name="label" />
      </label>
      <slot name="affix" />
      <span v-if="showCharactersRemaining" class="vcl-input__character-limit">{{ $t('charactersRemaining') }}: <span :class="(charactersRemaining >= 0) ? 'under-limit' : 'over-limit'">{{ charactersRemaining }}</span></span>
    </div>
    <div ref="inputContainer" class="vcl-input__input" :style="[{ height }, borderStyles]" :class="[{ 'is-disabled': $attrs.disabled },{ 'has-focus' : hasFocus }]" @keyup="keyup" @click="focus">
      <div v-if="$slots['prefix']" ref="prefix" class="vcl-input__input--prefix" :class="{ 'is-disabled': $attrs.disabled }" :style="{ height }">
        <slot name="prefix" />
      </div>
      <input
        :id="_uid+'VpInput'"
        ref="input"
        class="vcl-input__element"
        :class="{ 'vcl-input--error': $slots.error }"
        :style="inputStyles"
        :aria-label="ariaLabel"
        :role="role"
        v-bind="attrs"
        @input="$emit('input', $event.target.value, $attrs)"
        @keyup="keyup"
        @focus="focusGained"
        @blur="focusLost"
        v-on="listeners"
      >
      <div v-if="$slots['suffix']" class="vcl-input__input--suffix" :class="{ 'is-disabled': $attrs.disabled }">
        <slot name="suffix" />
      </div>
    </div>
    <span v-if="$slots.error" class="vcl-input__error-message">
      <slot name="error" />
    </span>
  </div>
</template>

<script>
export default {
  name: 'VpInput',
  props: {
    width: {
      type: String,
      default: '100%',
    },
    height: {
      type: String,
      default: '45px',
    },
    showCharactersRemaining: {
      type: Boolean,
      default: false,
    },
    styles: {
      type: Object,
      default() {
        return {};
      },
    },
    ariaLabel: {
      type: String,
      default: null,
    },
    role: {
      type: String,
      default: 'textbox'
    }
  },
  emits: ['input'],
  data() {
    return {
      hasFocus: false,
      inputLength: 0,
    };
  },
  computed: {
    borderStyles() {
      return {
        '--input-border': this.styles.border || '1px solid #AFAFAF',
        '--input-border-radius': this.styles.borderRadius || '6px',
        '--input-box-shadow': this.styles.boxShadow || '',
        '--input-background-color': this.styles.backgroundColor || '#fff',
        '--input-prefix-padding': this.styles.inputPrefixPadding || '0 8px',
        '--input-suffix-padding': this.styles.inputSuffixPadding || '0 8px',
        '--input-disabled-background-color': this.styles.disabledBackgroundColor || '#FBFBFB',
        '--input-cursor': this.styles.cursor || 'text',
        '--input-focus-color': this.styles.focusColor || '#016699',
      };
    },
    containerStyles() {
      const styles = {
        width: '100%',
      };
      if (this.styles.width) {
        styles['max-width'] = this.styles.width;
      }
      if (this.width) {
        styles['max-width'] = this.width;
      }
      return styles;
    },
    inputStyles() {
      const styles = {
        '--input-font-size': this.styles.fontSize || '16px',
        '--input-color': this.styles.color || '#4F4F4F',
        '--input-border-radius': this.styles.borderRadius || '6px',
        '--input-background-color': this.styles.backgroundColor || '#fff',
        '--input-placeholder-color': this.styles.placeholderColor || '#AFAFAF',
        '--input-disabled-color': this.styles.disabledColor || '#4F4F4F',
        '--input-disabled-background-color': this.styles.disabledBackgroundColor || '#F8F8F8',
        '--input-disabled-placeholder-color': this.styles.disabledPlaceholderColor || '#AFAFAF',
        '--input-user-select': this.styles.userSelect || 'auto',
        '--input-selection-background-color': this.styles.selectionBackgroundcolor || '#B4D5FE',
      };

      // if we have a passed padding value, use that
      if (this.styles.padding) {
        styles['--input-padding'] = this.styles.padding;
      } else if (this.$slots.prefix) {
        styles['--input-padding'] = '0 16px 0 0';
      } else {
        styles['--input-padding'] = '0 16px';
      }
      return styles;
    },
    listeners() {
      const { input, ...listeners } = this.$listeners;
      return listeners;
    },
    attrs() {
      const { class: classAttr, style, ...attrs } = this.$attrs;
      return attrs;
    },
    charactersRemaining() {
      if (this.$attrs.maxlength === undefined) { return ''; }

      return (this.$attrs.maxlength - this.inputLength);
    },
  },
  watch: {
    $attrs: {
      immediate: true,
      deep: true,
      handler(newAttrs) {
        if ((newAttrs.value === undefined) || (newAttrs.value === null)) {
          this.inputLength = 0;
        } else {
          this.inputLength = newAttrs.value.length;
        }
      }
    }
  },
  created() {
    if (this.$attrs.value !== undefined) {
      this.inputLength = (this.$attrs.value || '').length;
    } else { this.inputLength = 0; }
  },
  methods: {
    keyup(event) {
      this.inputLength = event.target.value.length;
    },
    focus() {
      this.$refs.input.focus();
    },
    focusGained() {
      this.hasFocus = true;
    },
    focusLost() {
      this.hasFocus = false;
    }
  },
};
</script>

<style lang="scss" scoped>
.vcl-input {
  $self: &;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
  width:100%;

 &__label {
  color:#4f4f4f;
  display: flex;
  margin: 0 0 4px 4px;
  box-sizing: border-box;
  white-space:normal;
  &.is-disabled {
    color:#afafaf;
  }
 }
 &__label-row {
  display:flex;
  justify-content: space-between;
  padding-right:8px;
  width:100%;
 }
 &__character-limit {
  color:#AFAFAF;
  font-size:14px;
 }
 &__input {
   $input: &;
   position: relative;
   display: flex;
   justify-content: flex-start;
   align-items: center;
   white-space: nowrap;
   border: 1px solid #c7c7c7;
   border: var(--input-border);
   border-radius: 6px;
   border-radius: var(--input-border-radius);
   box-shadow: 0 1px 3px rgba(0,0,0,.1);
   box-shadow: var(--input-box-shadow);
   background-color: #fff;
   background-color: var(--input-background-color);
   box-sizing: border-box;
   cursor: text;
   cursor: var(--input-cursor);
   width:100%;

   &.has-focus {
     border: 2px solid var(--input-focus-color);
   }

   &--prefix, &--suffix {
     display: flex;
     align-items: center;
     justify-content: center;
   }
   &--prefix {
     margin: 0;
     padding: var(--input-prefix-padding);
   }
   &--suffix {
     margin: 0;
     padding: var(--input-suffix-padding);
   }

   #{ $self }__element {
    width: 100%;
    height: 100%;
    margin: 1px 0 !important;
    border: none !important;
    box-shadow: none !important;
    font-size: 16px;
    font-size: var(--input-font-size);
    color: #4F4F4F;
    color: var(--input-color);
    border-radius: 6px;
    border-radius: var(--input-border-radius);
    background-color: #fff;
    background-color: var(--input-background-color);
    padding: 0 16px;
    padding: var(--input-padding);
    user-select: auto;
    user-select: var(--input-user-select);
    cursor: text;
    cursor: var(--input-cursor);

    &:focus {
      outline: none !important;
      border: none !important;
    }
    &::placeholder {
      font-size: 16px;
      font-size: var(--input-font-size);
      color: #AFAFAF;
      color: var(--input-placeholder-color);
    }
    &:disabled {
      background-color: #FBFBFB;
      background-color: var(--input-disabled-background-color);
      color: #DBDBDB;
      color: var(--input-disabled-color);
      &::placeholder {
        color: #AFAFAF;
        color: var(--input-disabled-placeholder-color);
      }
    }
    &::selection {
      background-color: #B4D5FE;
      background-color: var(--input-selection-background-color);
    }
    #{ $self }--error {
      border: 1px solid #CE3C31;
    }
   }
    &.is-disabled {
      background-color: #FBFBFB;
      background-color: var(--input-disabled-background-color);
    }
 }
 &__error-message {
  color: #CE3C31;
  font-size:12px;
  margin-left:4px;
 }
}

span.under-limit { color:#016699; }
span.over-limit { color:#CE3C31; }
</style>
